import type { ICoord2D, IRect, ITileGridCoord } from '@nova/types';

/** Inputs for nearest free top-left search of a sized rect inside an origin range. */
export interface FindNearestFreeRectOriginArgs {
  width: number;
  height: number;
  maxOriginX: number;
  maxOriginY: number;
  preferred: ICoord2D;
  blocked: readonly IRect[];
}

/** True when the point lies inside `rect` (half-open on max edges: [x, x+width) × [y, y+height)). */
export function pointInRect(point: ICoord2D, rect: IRect): boolean {
  return point.x >= rect.x
    && point.x < rect.x + rect.width
    && point.y >= rect.y
    && point.y < rect.y + rect.height;
}

/** True when two axis-aligned rects overlap (edge-touching does NOT count as overlap). */
export function rectsOverlap(a: IRect, b: IRect): boolean {
  return a.x < b.x + b.width
    && a.x + a.width > b.x
    && a.y < b.y + b.height
    && a.y + a.height > b.y;
}

/** Expands a footprint by `margin` on all sides. */
export function inflateRect(rect: IRect, margin: number): IRect {
  const m = Math.max(0, Math.floor(margin));
  return {
    x: rect.x - m,
    y: rect.y - m,
    width: rect.width + (m * 2),
    height: rect.height + (m * 2)
  };
}

/** True when inflated footprints do not overlap (each rect uses its own margin). */
export function rectsSeparatedByMargin(a: IRect, marginA: number, b: IRect, marginB: number): boolean {
  return !rectsOverlap(inflateRect(a, marginA), inflateRect(b, marginB));
}

/** True when `rect` sits at least `margin` inside the map bounds on every side. */
export function rectFitsMapWithMargin(mapWidth: number, mapHeight: number, rect: IRect, margin: number): boolean {
  const m = Math.max(0, Math.floor(margin));
  return rect.x >= m && rect.y >= m && rect.x + rect.width <= mapWidth - m && rect.y + rect.height <= mapHeight - m;
}

/** True when `rect` overlaps any entry in `others`. */
export function rectOverlapsAny(rect: IRect, others: readonly IRect[]): boolean {
  for (const other of others) {
    if (rectsOverlap(rect, other)) {
      return true;
    }
  }
  return false;
}

/**
 * Finds the top-left origin in [0..maxOriginX] × [0..maxOriginY] for a rect of the given size
 * that does not overlap `blocked`, preferring the Manhattan-nearest origin to `preferred`.
 */
export function findNearestFreeRectOrigin(args: FindNearestFreeRectOriginArgs): ICoord2D | null {
  let best: ICoord2D | null = null;
  let bestDist = Infinity;

  for (let y = 0; y <= args.maxOriginY; y++) {
    for (let x = 0; x <= args.maxOriginX; x++) {
      const candidate: IRect = {
        x,
        y,
        width: args.width,
        height: args.height
      };
      if (rectOverlapsAny(candidate, args.blocked)) {
        continue;
      }
      const dist = Math.abs(x - args.preferred.x) + Math.abs(y - args.preferred.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = { x, y };
      }
    }
  }

  return best;
}

/** Returns a copy of `local` translated by `origin`. */
export function translateRect(origin: ICoord2D, local: IRect): IRect {
  return {
    x: origin.x + local.x,
    y: origin.y + local.y,
    width: local.width,
    height: local.height
  };
}

/** Center point of an axis-aligned rect. */
export function rectCenter(rect: IRect): ICoord2D {
  return {
    x: rect.x + (rect.width / 2),
    y: rect.y + (rect.height / 2)
  };
}

/** True when `inner` lies entirely inside `outer`. */
export function rectContainsRect(outer: IRect, inner: IRect): boolean {
  return inner.x >= outer.x
    && inner.y >= outer.y
    && inner.x + inner.width <= outer.x + outer.width
    && inner.y + inner.height <= outer.y + outer.height;
}

/** Moves and/or shrinks `rect` so it fits inside `region`. */
export function clampRectInsideRegion(rect: IRect, region: IRect): IRect {
  const width = Math.min(rect.width, region.width);
  const height = Math.min(rect.height, region.height);
  const maxX = region.x + region.width - width;
  const maxY = region.y + region.height - height;
  return {
    x: Math.min(Math.max(rect.x, region.x), maxX),
    y: Math.min(Math.max(rect.y, region.y), maxY),
    width,
    height
  };
}

/** Grows `box` to contain `inner`, then clamps the result to `region`. */
export function expandRectToContain(box: IRect, inner: IRect, region: IRect): IRect {
  const minX = Math.min(box.x, inner.x);
  const minY = Math.min(box.y, inner.y);
  const maxX = Math.max(box.x + box.width, inner.x + inner.width);
  const maxY = Math.max(box.y + box.height, inner.y + inner.height);
  return clampRectInsideRegion({
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  }, region);
}

/** Smallest axis-aligned bounds covering both rects. */
export function unionRects(left: IRect, right: IRect): IRect {
  const minX = Math.min(left.x, right.x);
  const minY = Math.min(left.y, right.y);
  const maxX = Math.max(left.x + left.width, right.x + right.width);
  const maxY = Math.max(left.y + left.height, right.y + right.height);
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/** True when two rects share a full edge segment (corner-only touch is false). */
export function rectsShareEdge(left: IRect, right: IRect): boolean {
  const shareVertical = (left.x + left.width === right.x || right.x + right.width === left.x)
    && left.y < right.y + right.height
    && right.y < left.y + left.height;
  const shareHorizontal = (left.y + left.height === right.y || right.y + right.height === left.y)
    && left.x < right.x + right.width
    && right.x < left.x + left.width;
  return shareVertical || shareHorizontal;
}

/** Floors origin/size to integer tile units; width and height are at least 1. */
export function normalizeRectToIntegerTiles(rect: IRect): IRect {
  return {
    x: Math.floor(rect.x),
    y: Math.floor(rect.y),
    width: Math.max(1, Math.floor(rect.width)),
    height: Math.max(1, Math.floor(rect.height))
  };
}

/**
 * Chooses cols × rows whose product equals `count`, preferring cell aspect ratio close to the
 * region aspect ratio.
 */
export function chooseAspectAwareGridDimensions(
  count: number,
  regionWidth: number,
  regionHeight: number
): ITileGridCoord {
  let best: ITileGridCoord = { col: count, row: 1 };
  let bestScore = Number.POSITIVE_INFINITY;

  for (let cols = 1; cols <= count; cols++) {
    if (count % cols !== 0) {
      continue;
    }

    const rows = count / cols;
    const cellW = regionWidth / cols;
    const cellH = regionHeight / rows;
    const score = Math.abs(Math.log(cellW / cellH));
    if (score < bestScore) {
      bestScore = score;
      best = { col: cols, row: rows };
    }
  }

  return best;
}
